import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { addDays, isBefore } from 'date-fns';
import React, { useRef, useState } from 'react';
import { MdAttachFile, MdFileUpload, MdOpenInNew } from 'react-icons/md';
import { CheckPicker, Toggle } from 'rsuite';
import Modal, { ModalProps } from 'rsuite/lib/Modal';
import { ButtonPrimary, InputBox, LinkButton } from '../../../../../../../../components';
import { listClusters, listStores } from '../../../../../../../../components/FilterSection/services';
import { getProductSuppliersData } from '../../../../../../../../services/ProdutoService_ts';
import styles from './CriarNegociacaoArquivoModal.module.scss';

import { BlocoDaVerbaNoArquivo, useVerbaNoArquivo } from './BlocoDaVerbaNoArquivo';
import type { EscolhasDaVerbaNoArquivo } from '../../../verbaNoArquivo';
import { nivelPedeSelecao, NivelDoUpload, textosDaSelecaoDoNivel } from './nivelDoUpload';

type NivelType = NivelDoUpload;

export type CriarNegociacaoArquivoData = {
  name: string;
  supplier: string;
  startDate: Date;
  endDate: Date | null;
  enableFinalDate: boolean;
  nivel: NivelType | null;
  selectedIds: string[];
  file: File | null;
  /** Nulas quando a leitura do arquivo não voltou: sem ela não houve escolha a fazer. */
  escolhasDaVerba: EscolhasDaVerbaNoArquivo | null;
};

export type CriarNegociacaoArquivoModalProps = ModalProps & {
  onSubmit?: (data: CriarNegociacaoArquivoData) => void;
  onHide?: () => void;
};

const datePickerLocale = {
  sunday: 'Dom',
  monday: 'Seg',
  tuesday: 'Ter',
  wednesday: 'Qua',
  thursday: 'Qui',
  friday: 'Sex',
  saturday: 'Sáb',
  today: 'Hoje',
};

const nextDay = addDays(new Date(), 1);

const initialState: CriarNegociacaoArquivoData = {
  name: '',
  supplier: '',
  startDate: nextDay,
  endDate: null,
  enableFinalDate: false,
  nivel: null,
  selectedIds: [],
  file: null,
  escolhasDaVerba: null,
};

const downloadTemplate = () => {
  const link = document.createElement('a');
  link.href = 'https://s3.amazonaws.com/infoprice-prd-repository/ipa/file-negotiation/Template%20Upload%20de%20negocia%C3%A7%C3%A3o.xlsx';
  link.download = 'Template Upload de negociação.xlsx';
  link.click();
};

export const CriarNegociacaoArquivoModal = ({ className, onHide, onSubmit, ...props }: CriarNegociacaoArquivoModalProps) => {
  const [formData, setFormData] = useState<CriarNegociacaoArquivoData>(initialState);
  const [supplierQuery, setSupplierQuery] = useState('');
  const [itemsQuery, setItemsQuery] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // O que o sistema entendeu do arquivo escolhido, e o que o comprador decidiu a partir disso. Trocar
  // de arquivo relê e reavalia tudo, inclusive quando o segundo não tem a coluna que o primeiro tinha.
  const verba = useVerbaNoArquivo(formData.file);

  const { data: suppliers, isLoading: isLoadingSuppliers } = useQuery({
    queryKey: ['criar-negociacao-arquivo/suppliers', supplierQuery],
    initialData: [],
    queryFn: async () => {
      const response = await getProductSuppliersData({ search: supplierQuery });
      return response.map(item => ({ value: item.supplier, label: item.supplier }));
    },
  });

  const { data: clusters, isLoading: isLoadingClusters } = useQuery({
    queryKey: ['criar-negociacao-arquivo/clusters', itemsQuery],
    enabled: formData.nivel === 'CLUSTER',
    initialData: [],
    queryFn: () => listClusters(itemsQuery),
  });

  const { data: stores, isLoading: isLoadingStores } = useQuery({
    queryKey: ['criar-negociacao-arquivo/stores', itemsQuery],
    enabled: formData.nivel === 'LOJA',
    initialData: [],
    queryFn: () => listStores(itemsQuery),
  });

  const handleHide = () => {
    setFormData(initialState);
    setSupplierQuery('');
    setItemsQuery('');
    onHide?.();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setFormData(old => ({ ...old, file }));
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0] ?? null;
    if (file) setFormData(old => ({ ...old, file }));
  };

  const nivelItems = formData.nivel === 'CLUSTER' ? clusters : stores;
  const isLoadingItems = formData.nivel === 'CLUSTER' ? isLoadingClusters : isLoadingStores;

  // A pergunta e "o que este nivel pede?", e nao "e cluster?": no nivel de rede nao ha loja nem
  // cluster a escolher, porque a negociacao e a rede toda. O nivel nao afrouxa nada mais -- nome,
  // fornecedor e arquivo continuam exigidos, e o arquivo e o que a negociacao de fato precisa.
  const textosDaSelecao = textosDaSelecaoDoNivel(formData.nivel);
  const pedeSelecao = nivelPedeSelecao(formData.nivel);

  const isSubmitDisabled =
    !formData.name ||
    !formData.supplier ||
    !formData.nivel ||
    (pedeSelecao && !formData.selectedIds.length) ||
    !formData.file;

  return (
    <Modal
      className={classNames('info-modal', styles['criar-negociacao-arquivo-modal'], className)}
      onHide={handleHide}
      {...props}
    >
      <form onSubmit={e => e.preventDefault()}>
        <Modal.Header>
          <p className={styles['modal-title']}>Nova negociação com envio de arquivo</p>
          <p className={styles['modal-subtitle']}>Defina o contexto da negociação e importe os produtos via arquivo</p>
        </Modal.Header>

        <Modal.Body className={styles['modal-body']}>
          <InputBox className={styles['inputbox-name']}>
            <InputBox.Label>Nome da negociação</InputBox.Label>
            <InputBox.Input
              placeholder="Nome da negociação"
              value={formData.name}
              onChange={e => setFormData(old => ({ ...old, name: e.target.value }))}
            />
          </InputBox>

          <InputBox className={styles['inputbox-supplier']}>
            <InputBox.Label>Fornecedor</InputBox.Label>
            <InputBox.Select
              data={isLoadingSuppliers ? [] : suppliers}
              value={formData.supplier || null}
              placeholder="Selecione um fornecedor"
              onSearch={setSupplierQuery}
              onChange={(value: string) => setFormData(old => ({ ...old, supplier: value }))}
              locale={{
                searchPlaceholder: 'Busque um fornecedor',
                noResultsText: isLoadingSuppliers ? 'Carregando fornecedores...' : 'Nenhum fornecedor encontrado',
              }}
            />
          </InputBox>

          <p className={styles['vigencia-label']}>Vigência</p>

          <div className={styles['vigencia-container']}>
            <InputBox className={styles['inputbox-start']}>
              <InputBox.Label>Início de vigência</InputBox.Label>
              <InputBox.DatePicker
                block
                oneTap
                format="DD/MM/YYYY"
                ranges={[]}
                locale={datePickerLocale}
                value={formData.startDate}
                onChange={value => setFormData(old => ({ ...old, startDate: value as Date }))}
                disabledDate={date => isBefore(date as Date, new Date())}
                onClean={() => setFormData(old => ({ ...old, startDate: nextDay }))}
              />
            </InputBox>

            <InputBox className={styles['inputbox-end']}>
              <div className={styles['inputbox-end__header']}>
                <Toggle
                  size="sm"
                  checked={formData.enableFinalDate}
                  onChange={checked =>
                    setFormData(old => ({ ...old, enableFinalDate: checked, endDate: checked ? old.endDate : null }))
                  }
                />
                <InputBox.Label>Final de vigência</InputBox.Label>
              </div>
              <InputBox.DatePicker
                block
                oneTap
                format="DD/MM/YYYY"
                ranges={[]}
                locale={datePickerLocale}
                disabled={!formData.enableFinalDate}
                value={formData.endDate ?? undefined}
                onChange={value => setFormData(old => ({ ...old, endDate: value as Date | null }))}
                disabledDate={date => isBefore(date as Date, formData.startDate)}
                onClean={() => setFormData(old => ({ ...old, endDate: null }))}
              />
            </InputBox>
          </div>

          <p className={styles['section-label']}>Abrangência</p>

          <InputBox className={styles['inputbox-nivel']}>
            <InputBox.Label>Nível</InputBox.Label>
            <InputBox.Select
              data={[
                { value: 'LOJA', label: 'Loja' },
                { value: 'CLUSTER', label: 'Cluster' },
                // O mesmo texto que o seletor de agrupamento da grade usa. Nao inventar terceira
                // grafia: o produto ja tem "Toda a Rede" no seletor e "REDE TODA" no selo da lista.
                { value: 'REDE', label: 'Toda a Rede' },
              ]}
              value={formData.nivel}
              placeholder="Selecione um nível"
              searchable={false}
              onChange={(value: NivelType) => setFormData(old => ({ ...old, nivel: value, selectedIds: [] }))}
            />
          </InputBox>

          {textosDaSelecao && (
            <InputBox className={styles['inputbox-items']}>
              <InputBox.Label>{textosDaSelecao.rotulo}</InputBox.Label>
              <CheckPicker
                block
                data={isLoadingItems ? [] : nivelItems}
                value={formData.selectedIds}
                placeholder={textosDaSelecao.placeholder}
                onChange={(values: string[]) => setFormData(old => ({ ...old, selectedIds: values }))}
                onSearch={setItemsQuery}
                onClose={() => setItemsQuery('')}
                renderValue={(values, items) =>
                  (items as { label: string }[]).map(item => item.label).join(', ')
                }
                locale={{
                  searchPlaceholder: textosDaSelecao.buscaPlaceholder,
                  noResultsText: isLoadingItems ? 'Carregando...' : 'Nenhum resultado encontrado',
                  checkAll: 'Selecionar todos',
                  placeholder: 'Buscar...',
                }}
              />
            </InputBox>
          )}

          <div
            className={styles['upload-area']}
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={e => e.preventDefault()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xls,.xlsx,.csv"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <MdAttachFile className={styles['upload-icon']} />
            <p className={styles['upload-title']}>Novo envio de arquivo</p>
            <p className={styles['upload-desc']}>
              {/*
                Os três formatos que o campo acima aceita, na mesma redação do outro modal de
                arquivo. A lista dizia dois, e o modelo que esta tela oferece para baixar é .xlsx --
                o formato que a copy esquecia era justamente o do modelo.
              */}
              Envie seu arquivo em formato .csv, .xls ou .xlsx.
              <br />
              Arraste o arquivo aqui ou clique para selecionar.
            </p>
            {formData.file ? (
              <div className={styles['upload-file-row']}>
                <MdAttachFile className={styles['upload-file-icon']} />
                {/*
                  O cartão é onde a detecção da coluna fica visível: a leitura da planilha é
                  posicional, e este é o único lugar em que o comprador vê o que o sistema entendeu do
                  arquivo dele antes de escolher.
                */}
                <span className={styles['upload-file-info']}>
                  <span className={styles['upload-file-name']} title={formData.file.name}>
                    {formData.file.name}
                  </span>
                  {verba.linhaDoCartao ? <span className={styles['upload-file-leitura']}>{verba.linhaDoCartao}</span> : null}
                </span>
                <span className={styles['upload-file-size']}>{(formData.file.size / 1024).toFixed(0)}KB</span>
                <button
                  type="button"
                  className={styles['upload-file-remove']}
                  onClick={e => {
                    e.stopPropagation();
                    setFormData(old => ({ ...old, file: null }));
                  }}
                >
                  ×
                </button>
              </div>
            ) : (
              <button
                type="button"
                className={styles['upload-btn']}
                onClick={e => {
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
              >
                <MdAttachFile />
                Adicionar arquivo
              </button>
            )}
          </div>

          <LinkButton className={styles['download-template']} onClick={downloadTemplate}>
            <MdOpenInNew />
            Baixe o modelo de arquivo
          </LinkButton>

          <BlocoDaVerbaNoArquivo
            leitura={verba.leitura}
            estado={verba.estado}
            onDestino={verba.escolherODestino}
            onDestinoDasVazias={verba.escolherAsVazias}
            idDoGrupo="criar-negociacao"
            className={styles['bloco-da-verba']}
          />
        </Modal.Body>

        <Modal.Footer>
          <ButtonPrimary skin="blue" theme="ghost" type="button" onClick={handleHide}>
            CANCELAR
          </ButtonPrimary>
          <ButtonPrimary
            skin="blue"
            theme="filled"
            type="submit"
            disabled={isSubmitDisabled}
            onClick={() => onSubmit?.({ ...formData, escolhasDaVerba: verba.escolhas })}
          >
            SALVAR
          </ButtonPrimary>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
